/**
 * Multi-tenancy context and authorization types.
 * Every operation runs within a TenantContext that enforces org isolation and role-based access.
 */
/**
 * KAN-115 — the roles a person ACTS with, given the roles they HOLD.
 *
 * Owner ruling (Taylor, 2026-09-25, final): "Assistant coaches get the same powers as coaches for
 * now, until the team roles in KAN-128 are built." So an assistant coach acts as a coach: 'coach'
 * is added after the roles they hold. Nothing else changes:
 *   - the held roles come back first, in their order, so `roles[0]` (what the API records as the
 *     viewer's role, and what the apps label someone as) is still 'assistant_coach';
 *   - no other role is ever added — never admin, owner or a platform role;
 *   - someone who already holds 'coach' gets it once, not twice; every other list is unchanged.
 *
 * Team scoping is untouched, in both directions. Where a check limits a coach to the teams they
 * coach (their team_coaches row: isTeamCoach, the kiosk, match analysis), an assistant coach's own
 * team_coaches row decides the same way. Where a check admits any coach club-wide, it admits an
 * assistant coach club-wide too — exactly a coach's reach, never more.
 *
 * This is the ONE rule. The API applies it where a verified token becomes a role list
 * (verifyToken, apps/api/src/middleware/auth.ts); the web and mobile apps apply it where they read
 * the session's roles. Use it only where roles decide access. A place that SHOWS or STORES a
 * role must read the held roles, not these.
 *
 * When KAN-128 lands, delete this and let team roles decide.
 */
export const actingRoles = (held) => {
    // A malformed claim grants NOTHING. This is the one place every surface turns a claim into the
    // roles it acts with, so it is the place to fail closed: passed through, a string claim reaches
    // `roles.includes(...)` as String.prototype.includes — a SUBSTRING test — so 'assistant_coach'
    // read as holding 'coach' and any string containing 'owner' passed isAdmin(). Non-string entries
    // are dropped for the same reason.
    if (!Array.isArray(held))
        return [];
    const roles = held.filter((r) => typeof r === 'string');
    if (roles.includes('assistant_coach') && !roles.includes('coach'))
        roles.push('coach');
    return roles;
};
/**
 * THE reading of "this login is a player, and nothing staff", on a role list: it holds 'player' and
 * acts as neither an admin (admin or owner) nor a coach (through {@link actingRoles}, so an assistant
 * coach counts as a coach). Holding 'parent' as well changes nothing: it cannot be told apart from a
 * child's login (KAN-239/241/243/245), so it fails closed. Fails closed the way actingRoles does
 * too: a malformed claim holds nothing.
 *
 * This is the ONE copy. Who asks it:
 *   - on a token: the footage gate (KAN-221, apps/api services/child-login.ts isChildLogin), the
 *     tryout gate (KAN-245, services/tryout-access.ts), the web app's parent-page gate and dashboard
 *     (apps/web utils/userRoles.ts) and the mobile app's tab choice (apps/mobile utils/roles.ts);
 *   - on a membership: the KAN-143 child-account rule (@kantera/db findChildAccountIds), which
 *     applies it to the login's membership OF THE CLUB BEING ACTED IN when the login has no player
 *     record of its own (an own record decides first: 18+ an adult whatever the roles). That rule,
 *     not this, decides parent and guardian standing in the API.
 */
export const isChildLoginRoles = (held) => {
    const roles = actingRoles(held);
    return (roles.includes('player') &&
        !roles.includes('admin') &&
        !roles.includes('owner') &&
        !roles.includes('coach'));
};
/**
 * Check if a role has a permission.
 * Define once; use everywhere for consistency.
 */
export const hasPermission = (role, permission) => {
    const rolePermissions = {
        owner: ['view', 'create', 'update', 'delete', 'admin'],
        admin: ['view', 'create', 'update', 'delete'],
        coach: ['view', 'create', 'update'],
        // KAN-115: exactly a coach's, until KAN-128's team roles exist (see actingRoles).
        assistant_coach: ['view', 'create', 'update'],
        // team_manager: pending-record creation only (rsvps, comms, draft card/goal
        // reports). No update/delete on canonical data — coach must confirm.
        team_manager: ['view', 'create'],
        parent: ['view'],
        // referee: can create + update match-time records (cards, goals, scores)
        // for matches they're assigned to. No delete.
        referee: ['view', 'create', 'update'],
        player: ['view'],
        // Platform-level roles have full permissions within their own scope (cross-org admin panel)
        platform_admin: ['view', 'create', 'update', 'delete', 'admin'],
        founder: ['view', 'create', 'update', 'delete', 'admin'],
    };
    return rolePermissions[role].includes(permission);
};
//# sourceMappingURL=tenant.js.map